import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarContext from "../../contexts/MyContext";
import { AudioHandler } from "../../Apihander/AudioRecording/AudioRecording";
import { errorToast, successToast } from "../../utils/notificationUtils";
import { micErrorMessage } from "../../utils/micErrorMessage";
import { formatTimeInHHMMSS } from "../../utils/timeFile";
import { generateRandomNumber } from "../../utils/generateRandomNumber";
import { convertToCaf, convertToMp3, convertToWav } from "../RecordingFeatures/convertToMp3";
import { NoteEditorModal } from "./NotesModals";
import { ConfirmDialog, EMPTY_NOTE_TEXT, Ic, PageHeader, Spinner, fmt, newClientId, readErrorMessage } from "./notesShared";
import "./notes.css";
import "../../css/app/recorder.css";

// Free accounts stop at five minutes, the same limit as the Record new tab.
const FREE_LIMIT_SECONDS = 300;

const convertRecording = (blob, format) => {
    if (format === "mp3") return convertToMp3(blob);
    if (format === "wav") return convertToWav(blob);
    if (format === "caf") return convertToCaf(blob);
    if (format === "m4a") return Promise.resolve(blob);
    return Promise.reject(new Error("Unsupported audio format"));
};

const WAVE_BARS = 38;

// Animated bars while recording, a still shape otherwise, as in the HTML reference.
const Wave = ({ live }) => {
    const now = Date.now();
    return (
        <div className={`nt-wave ${live ? "live" : ""}`}>
            {Array.from({ length: WAVE_BARS }, (_, i) => (
                <b
                    key={i}
                    style={{
                        height: `${live
                            ? 12 + Math.round(Math.abs(Math.sin(i * 0.7 + now / 260)) * 74)
                            : 10 + Math.round(Math.abs(Math.sin(i * 0.55)) * 26)}%`,
                    }}
                />
            ))}
        </div>
    );
};

// Also the Record new tab, where it waits for the red button unless autoStart is set.
const RecordWithNotes = ({ onSaved, autoStart = true, folderId = null }) => {
    const { selectedFormat, user } = useContext(SidebarContext);
    const navigate = useNavigate();
    const [status, setStatus] = useState("idle");
    const [seconds, setSeconds] = useState(0);
    const [notes, setNotes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [deletingNote, setDeletingNote] = useState(null);
    const [, setWaveTick] = useState(0);

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const secondsRef = useRef(0);
    // The upload runs in the recorder's onstop closure, so notes are mirrored in a ref.
    const notesRef = useRef([]);
    const discardRef = useRef(false);
    const unmountedRef = useRef(false);
    // StrictMode runs mount effects twice, so the auto start is allowed only once.
    const autoStartedRef = useRef(false);

    // Once Stop is pressed the screen shows saving, not recording.
    const live = status === "recording" && !saving;
    const isFree = !user?.isSubscriptionActive;

    useEffect(() => {
        notesRef.current = notes;
    }, [notes]);

    useEffect(() => {
        if (!live) return undefined;
        const waveTimer = setInterval(() => setWaveTick(Date.now()), 160);
        return () => clearInterval(waveTimer);
    }, [live]);

    const releaseStream = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    };

    // Leaving the screen mid-recording drops the recording.
    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            clearInterval(timerRef.current);
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                discardRef.current = true;
                recorderRef.current.stop();
            }
            releaseStream();
        };
    }, []);

    const uploadRecording = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/mp4" });
        chunksRef.current = [];
        const draftNotes = notesRef.current;

        try {
            const processed = await convertRecording(blob, selectedFormat);
            const formData = new FormData();
            formData.append("audiofile", processed, `Voice${generateRandomNumber()}.${selectedFormat}`);
            formData.append("timeDuration", formatTimeInHHMMSS(secondsRef.current));
            formData.append("action", "Audio");
            formData.append("isFavourite", false);
            formData.append("hasnote", "false");
            if (draftNotes.length) {
                formData.append("notes", JSON.stringify(draftNotes.map((note) => ({
                    clientId: note.clientId,
                    content: note.content,
                    timestampMs: note.timestampSec * 1000,
                }))));
            }

            const uploaded = await AudioHandler.doUploadAudioUrl(formData, folderId);
            if (uploaded?.statusCode === 200) {
                const count = draftNotes.length;
                successToast(count ? `Recording saved with ${count} note${count > 1 ? "s" : ""}` : "Recording saved");
                onSaved(uploaded?.data);
            } else {
                errorToast(readErrorMessage(uploaded));
                setSaving(false);
            }
        } catch (error) {
            errorToast(readErrorMessage(error));
            setSaving(false);
        }
    };

    const startTimer = () => {
        timerRef.current = setInterval(() => {
            secondsRef.current += 1;
            setSeconds(secondsRef.current);
            if (secondsRef.current >= FREE_LIMIT_SECONDS && isFree) {
                stopRecording();
                navigate("/pricing");
            }
        }, 1000);
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (unmountedRef.current) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }
            streamRef.current = stream;
            const recorder = new MediaRecorder(stream, { mimeType: "audio/mp4" });
            recorderRef.current = recorder;
            chunksRef.current = [];
            discardRef.current = false;

            recorder.ondataavailable = (event) => chunksRef.current.push(event.data);
            recorder.onstop = () => {
                releaseStream();
                if (discardRef.current || !chunksRef.current.length) return;
                uploadRecording();
            };

            recorder.start();
            secondsRef.current = 0;
            setSeconds(0);
            setStatus("recording");
            startTimer();
        } catch (error) {
            console.error("Failed to start recording", error);
            errorToast(micErrorMessage(error));
            releaseStream();
        }
    };

    const toggleRecording = () => {
        const recorder = recorderRef.current;
        if (status === "idle") {
            startRecording();
        } else if (status === "recording") {
            recorder?.pause();
            clearInterval(timerRef.current);
            setStatus("paused");
        } else {
            recorder?.resume();
            startTimer();
            setStatus("recording");
        }
    };

    // Adds a blank note at the current second; the ref is updated right away so an upload that follows includes it.
    const addBlankNote = () => {
        notesRef.current = [...notesRef.current, { clientId: newClientId(), content: "", timestampSec: secondsRef.current }];
        setNotes(notesRef.current);
        successToast("Note added");
    };

    function stopRecording() {
        if (secondsRef.current < 1) {
            errorToast("Nothing recorded yet");
            return;
        }
        clearInterval(timerRef.current);
        setSaving(true);
        recorderRef.current?.stop();
    }

    // Opening this screen starts recording straight away.
    useEffect(() => {
        if (!autoStart || autoStartedRef.current) return;
        autoStartedRef.current = true;
        startRecording();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortedNotes = [...notes].sort((a, b) => a.timestampSec - b.timestampSec);
    const active = status !== "idle";
    // Visual state for the recorder styles only.
    const stageState = saving ? "is-saving" : live ? "is-live" : active ? "is-paused" : "is-idle";

    return (
        <>
            <PageHeader title="New Recording" />
            <div className="nt-content vr-rec-content">
                <div className={`nt-rec vr-rec ${stageState}`}>
                    <div className="vr-rec-stage">
                        <div className="nt-recwrap">
                            <div className={`nt-recstate ${live ? "live" : ""}`} role="status">
                                {live ? <><i />RECORDING</> : saving ? "Saving…" : active ? "Paused" : "Ready to record"}
                            </div>
                            <img className="vr-rec-hero" src={live ? "/img/hero/play.gif" : "/img/hero/animation.png"} alt="Voice-Recorder-Frequency" width={220} height={220} />
                            <div className="nt-timer">
                                {fmt(seconds)}{isFree && <small> / 05:00</small>}
                            </div>
                        </div>
                        <Wave live={live} />

                        <div className="nt-transport">
                            <button type="button" className="nt-tbtn vr-rec-side" title="Add a note at this moment" aria-label="Add a note at this moment" disabled={!active || saving} onClick={addBlankNote}>
                                <Ic name="noteplus" size="l" />
                            </button>
                            <button type="button" className={`nt-tbtn big${live ? " is-live" : ""}`} title={live ? "Pause" : "Record"} aria-label={live ? "Pause" : "Record"} disabled={saving} onClick={toggleRecording}>
                                <Ic name={live ? "pause" : "mic"} size="xl" />
                            </button>
                            <button type="button" className="nt-tbtn vr-rec-side vr-rec-stop" title="Stop and save" aria-label="Stop and save" disabled={!active || saving} onClick={stopRecording}>
                                {saving ? <Spinner /> : <Ic name="stop" size="l" />}
                            </button>
                        </div>
                        <p className="nt-hint">
                            {saving
                                ? "Saving your recording…"
                                : active
                                    ? (live ? "Click the note button to add a note at this moment" : "Paused — click to resume")
                                    : "Click the red button to start recording"}
                        </p>
                        {saving && <div className="vr-progress is-indeterminate vr-rec-saving"><i /></div>}
                    </div>

                    {sortedNotes.length > 0 && (
                        <div className="vr-rec-moments">
                            <div className="nt-sectitle nt-caps">Moments · {sortedNotes.length}</div>
                            {sortedNotes.map((note) => (
                                <div className="nt-note" key={note.clientId}>
                                    <span className="nt-ts">{fmt(note.timestampSec)}</span>
                                    <span className="nt-txt">{note.content || EMPTY_NOTE_TEXT}</span>
                                    <span className="nt-acts">
                                        <button type="button" className="nt-iconbtn" aria-label="Edit note" disabled={saving} onClick={() => setEditingNote(note)}><Ic name="edit" size="s" /></button>
                                        <button type="button" className="nt-iconbtn" aria-label="Delete note" disabled={saving} onClick={() => setDeletingNote(note)}><Ic name="trash" size="s" /></button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {editingNote && (
                <NoteEditorModal
                    timestampSec={editingNote.timestampSec}
                    initialValue={editingNote.content}
                    onClose={() => setEditingNote(null)}
                    onSave={(text) => {
                        setNotes((prev) => prev.map((note) => (note.clientId === editingNote.clientId ? { ...note, content: text } : note)));
                        setEditingNote(null);
                        successToast("Note updated");
                    }}
                />
            )}
            {deletingNote && (
                <ConfirmDialog
                    title="Delete note?"
                    message="This note will be permanently deleted."
                    onCancel={() => setDeletingNote(null)}
                    onConfirm={() => {
                        setNotes((prev) => prev.filter((note) => note.clientId !== deletingNote.clientId));
                        setDeletingNote(null);
                        successToast("Note deleted");
                    }}
                />
            )}
        </>
    );
};

export default RecordWithNotes;
